import React, { useState, useRef, useEffect } from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	LinearProgress,
	Chip,
	Divider,
} from "@mui/material";
import {
	ArrowBack as ArrowBackIcon,
	PlayArrow as PlayArrowIcon,
	Cancel as CancelIcon,
	Description as FileIcon,
	Map as ZoneIcon,
	Warehouse as FacilityIcon,
} from "@mui/icons-material";
import { runAllocation, getJob, cancelJob } from "./services/api";

function RunAllocation({
	uploadedFile,
	zoneMapping,
	facilityMapping,
	selectedFacilities,
	onComplete,
	onError,
	loading,
	setLoading,
	onBack,
}) {
	const [progress, setProgress] = useState(0);
	const [cancelling, setCancelling] = useState(false);
	const [activeJobId, setActiveJobId] = useState(null);
	const pollTimeoutRef = useRef(null);
	const zoneCount = Object.keys(zoneMapping || {}).length;
	const facilityCount = (selectedFacilities || []).length;

	useEffect(() => {
		return () => {
			if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
		};
	}, []);

	const POLL_INTERVAL_MS = 2500;
	const handleRun = async () => {
		if (!uploadedFile) {
			onError("No file uploaded");
			return;
		}

		try {
			setLoading(true);
			setProgress(0);
			onError("");

			const body = {
				zone_mapping: zoneMapping,
				facility_mapping: facilityMapping || {},
				selected_facilities: selectedFacilities,
				allocation_strategy: "PROPORTIONAL",
			};
			if (uploadedFile?.job_id) {
				body.job_id = uploadedFile.job_id;
			} else {
				body.filepath = uploadedFile?.filepath;
			}
			const response = await runAllocation(body);

			// Backend returns 202 and starts job in background (Replenishment-style)
			if (response.status === 202 && response.data?.job_id) {
				const jobId = response.data.job_id;
				setActiveJobId(jobId);
				const poll = async () => {
					try {
						const res = await getJob(jobId);
						const job = res.data?.job;
						if (!job) return;
						if (job.stage === "COMPLETED") {
							setProgress(100);
							const result = job.result?.summary
								? job.result
								: { summary: {}, output_file: job.output_file_path?.split("/").pop() || "" };
							onComplete(result);
							setLoading(false);
							setActiveJobId(null);
							return;
						}
						if (job.stage === "FAILED") {
							onError(job.result?.error || "Allocation failed");
							setLoading(false);
							setActiveJobId(null);
							return;
						}
						if (job.stage === "CANCELLED") {
							onError("Job was cancelled");
							setLoading(false);
							setActiveJobId(null);
							setCancelling(false);
							return;
						}
						setProgress((p) => Math.min(p + 5, 90));
						pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
					} catch (err) {
						onError(err.response?.data?.error || err.message || "Error checking job status");
						setLoading(false);
						setActiveJobId(null);
					}
				};
				pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				return;
			}

			// Fallback: old sync response (200 + full result)
			if (response.data?.success && response.data?.summary) {
				setProgress(100);
				onComplete(response.data);
			} else {
				onError(response.data?.error || "Allocation failed");
			}
			setLoading(false);
		} catch (error) {
			onError(
				error.response?.data?.error ||
					error.message ||
					"Error running allocation"
			);
			setLoading(false);
		}
	};

	const handleCancel = async () => {
		if (!activeJobId) return;
		setCancelling(true);
		try {
			await cancelJob(activeJobId);
		} catch (err) {
			console.error("Cancel failed:", err);
			setCancelling(false);
		}
	};

	return (
		<Box>
			<Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
				Ready to Run Allocation
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Review the summary below and click Run Allocation to process your requirements.
			</Typography>

			<Paper
				variant="outlined"
				sx={{
					p: 3,
					mb: 3,
					borderRadius: 2,
					bgcolor: "grey.50",
					border: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
					Summary
				</Typography>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
							<FileIcon sx={{ fontSize: 20, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={600}>
								File
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
							{uploadedFile?.filename || "—"}
						</Typography>
						{uploadedFile?.total_records != null && (
							<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
								{uploadedFile.total_records} record{uploadedFile.total_records !== 1 ? "s" : ""}
							</Typography>
						)}
					</Box>
					<Divider />
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
							<ZoneIcon sx={{ fontSize: 20, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={600}>
								Zone mapping
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							{zoneCount} zone{zoneCount !== 1 ? "s" : ""} configured
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{Object.keys(zoneMapping || {}).map((zone) => (
								<Chip key={zone} label={zone} size="small" variant="outlined" />
							))}
						</Box>
					</Box>
					<Divider />
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
							<FacilityIcon sx={{ fontSize: 20, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={600}>
								Selected facilities
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							{facilityCount} sub-facilit{facilityCount !== 1 ? "ies" : "y"} selected
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{(selectedFacilities || []).map((f) => (
								<Chip key={f} label={f} size="small" color="primary" variant="outlined" />
							))}
						</Box>
					</Box>
				</Box>
			</Paper>

			{loading && (
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
						<Typography variant="subtitle2" color="text.secondary">
							{cancelling ? "Cancelling…" : "Job started. You can leave the page or wait here for completion."}
						</Typography>
						<Button
							size="small"
							color="error"
							variant="outlined"
							startIcon={<CancelIcon />}
							onClick={handleCancel}
							disabled={cancelling || !activeJobId}
						>
							{cancelling ? "Cancelling…" : "Cancel"}
						</Button>
					</Box>
					<LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
						Checking status… {progress}%
					</Typography>
				</Paper>
			)}

			<Box display="flex" justifyContent="space-between" mt={4}>
				<Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined" disabled={loading}>
					Back
				</Button>
				<Button
					startIcon={!loading && <PlayArrowIcon />}
					onClick={handleRun}
					variant="contained"
					disabled={loading || !uploadedFile}
					size="large"
				>
					{loading ? "Processing…" : "Run Allocation"}
				</Button>
			</Box>
		</Box>
	);
}

export default RunAllocation;
